import {
	cancelAdminUserSenderRun,
	fetchAdminUserSenderRun,
	fetchAdminUserSenderRunEvents,
	resumeAdminUserSenderRun,
} from '@/api/admin';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { useSenderRunDetail } from '@/hooks/use-sender-run-detail';
import EntityHeroCard from '@/components/navigation/entity-hero-card';
import { PageScroll } from '@/layouts/page-layout';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import SenderRunActionConfirmDialog, {
	type SenderRunAdminAction,
} from '@/pages/admin/users/detail/sender/sender-run-action-confirm-dialog';
import SenderRunDetailView from '@/pages/sender/sender-run-detail-view';
import { ArrowLeftIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

export default function AdminUserSenderRunDetailPage() {
	const params = useParams();
	const navigate = useNavigate();
	const profileId = Number(params.profileId);
	const runId = params.runId ?? '';
	const [cancelling, setCancelling] = useState(false);
	const [resuming, setResuming] = useState(false);
	const [pendingAction, setPendingAction] = useState<SenderRunAdminAction | null>(null);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const loadRun = useCallback(async () => {
		if (!Number.isFinite(profileId) || !runId) throw new Error('missing params');
		return fetchAdminUserSenderRun(profileId, runId);
	}, [profileId, runId]);

	const loadEvents = useCallback(async () => {
		if (!Number.isFinite(profileId) || !runId) throw new Error('missing params');
		const data = await fetchAdminUserSenderRunEvents(profileId, runId, { limit: 100 });
		return data.items;
	}, [profileId, runId]);

	const {
		run,
		events,
		loading,
		loadData,
		bumpGeneration,
		applyOptimisticCancel,
		applyOptimisticResume,
	} = useSenderRunDetail({
		enabled: Number.isFinite(profileId) && Boolean(runId),
		loadRun,
		loadEvents,
	});

	const handleBack = useCallback(() => {
		navigate(`${ROUTES.adminUser}${profileId}?tab=sender`);
	}, [navigate, profileId]);

	const openConfirm = useCallback((action: SenderRunAdminAction) => {
		setPendingAction(action);
		setConfirmOpen(true);
	}, []);

	const handleConfirmAction = useCallback(async () => {
		if (!Number.isFinite(profileId) || !runId || !pendingAction) return;

		if (pendingAction === 'cancel') {
			setCancelling(true);
			try {
				await cancelAdminUserSenderRun(profileId, runId);
				bumpGeneration();
				applyOptimisticCancel();
				toast.success('Запуск отменён');
				setConfirmOpen(false);
				await loadData();
			} catch {
				// error toast handled by api interceptor
			} finally {
				setCancelling(false);
			}
			return;
		}

		setResuming(true);
		try {
			await resumeAdminUserSenderRun(profileId, runId);
			bumpGeneration();
			applyOptimisticResume();
			toast.success('Рассылка возобновлена');
			setConfirmOpen(false);
		} catch {
			// error toast handled by api interceptor
		} finally {
			setResuming(false);
		}
	}, [profileId, runId, pendingAction, loadData, bumpGeneration, applyOptimisticCancel, applyOptimisticResume]);

	if (!Number.isFinite(profileId) || !runId) {
		return <div className='p-4 text-muted-foreground text-sm'>Некорректные параметры</div>;
	}

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-3'>
			<EntityHeroCard
				title='Sender run'
				subtitle={run?.caption ? run.caption.slice(0, 80) : runId}
				trailing={
					<Button type='button' variant='outline' size='sm' onClick={handleBack}>
						<ArrowLeftIcon size={14} className='mr-1' />
						Назад
					</Button>
				}
			/>

			{loading ? (
				<div className='flex justify-center py-16'>
					<Spinner />
				</div>
			) : !run ? (
				<LandingsErrorState onRetry={() => void loadData(true)} />
			) : (
				<SenderRunDetailView
					run={run}
					events={events}
					actions={{
						onCancel: () => openConfirm('cancel'),
						onResume: () => openConfirm('resume'),
						cancelling,
						resuming,
					}}
				/>
			)}

			<SenderRunActionConfirmDialog
				open={confirmOpen}
				onOpenChange={setConfirmOpen}
				action={pendingAction}
				run={run}
				onConfirm={handleConfirmAction}
				loading={cancelling || resuming}
			/>
		</PageScroll>
	);
}
